import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@trillion/ui/components/ui/alert";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@trillion/ui/components/ui/tabs";
import { getEnv } from "@trillion/ui/lib/env";
import { ThemeToggle } from "@trillion/ui/theme/theme-toggle";
import { AlertTriangle, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { CopyableCode } from "../components/copyable-code";
import {
  boardMcpCapabilities,
  boardMcpPermissions,
  buildBoardMcpClientRecipes,
  resolveBoardMcpEndpoint,
} from "../lib/board-mcp-guide";

function BulletList({ items }: { items: readonly string[] }) {
  return (
    <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function McpPage() {
  const endpointResult = resolveBoardMcpEndpoint(getEnv("BOARD_API_URL"));

  if (!endpointResult.ok) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-3xl items-center p-4 sm:p-8">
        <Alert variant="destructive">
          <AlertTriangle aria-hidden="true" />
          <AlertTitle>Board MCP setup is unavailable</AlertTitle>
          <AlertDescription>{endpointResult.message}</AlertDescription>
        </Alert>
      </main>
    );
  }

  const { endpoint } = endpointResult;
  const recipes = buildBoardMcpClientRecipes(endpoint);

  return (
    <main className="min-h-screen min-w-0 bg-background text-foreground">
      <div className="mx-auto w-full min-w-0 max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="mb-10 flex min-w-0 flex-col gap-6 border-b pb-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-3">
            <p className="text-sm font-medium text-primary">Trillion Board</p>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Board MCP setup</h1>
            <p className="max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Connect a qualified MCP client to the Board tools and resources your Trillion Passport
              account can already access.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button asChild variant="outline">
              <Link to="/login">Sign in to Board</Link>
            </Button>
            <ThemeToggle />
          </div>
        </header>

        <div className="space-y-8">
          <Alert>
            <CheckCircle2 aria-hidden="true" />
            <AlertTitle>Development-qualified client recipes</AlertTitle>
            <AlertDescription>
              These exact client versions passed local development qualification. Production support
              remains pending until the same recipes and deployed transport are replayed in the release
              checklist.
            </AlertDescription>
          </Alert>

          <section aria-labelledby="endpoint-heading" className="grid min-w-0 gap-6 lg:grid-cols-2">
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle>
                  <h2 id="endpoint-heading">Your Board MCP endpoint</h2>
                </CardTitle>
                <CardDescription>
                  Every recipe below is generated from this Board environment.
                </CardDescription>
              </CardHeader>
              <CardContent className="min-w-0">
                <CopyableCode code={endpoint} label="Board MCP endpoint" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>Before you connect</h2>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BulletList
                  items={[
                    "A Trillion Passport account and a browser available for authorization.",
                    "Existing access to the Board diagrams or teams you intend to use.",
                    "A separate client profile when you want an independent connection or identity.",
                    "Permission to open a loopback callback listener on your computer.",
                  ]}
                />
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  The <code>board:mcp offline_access</code> grant enables the existing Board tools and
                  refresh lifecycle. It does not bypass a diagram's share list, team membership, or
                  revision-conflict checks.
                </p>
              </CardContent>
            </Card>
          </section>

          <section aria-labelledby="capabilities-heading" className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2 id="capabilities-heading">What you can do</h2>
                </CardTitle>
                <CardDescription>Board exposes 13 tools plus diagram and team resources.</CardDescription>
              </CardHeader>
              <CardContent>
                <BulletList items={boardMcpCapabilities} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>Permissions still apply</h2>
                </CardTitle>
                <CardDescription>
                  MCP uses the same authorization rules as the Board application.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BulletList items={boardMcpPermissions} />
              </CardContent>
            </Card>
          </section>

          <section aria-labelledby="recipes-heading" className="min-w-0 space-y-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight" id="recipes-heading">
                Choose your client
              </h2>
              <p className="mt-2 text-muted-foreground">
                Use the native configuration, authorization, inspection, and cleanup commands for the
                exact version named on each tab.
              </p>
            </div>

            <Tabs className="min-w-0" defaultValue={recipes[0].id}>
              <div className="min-w-0 overflow-x-auto pb-1">
                <TabsList aria-label="Supported Board MCP clients">
                  {recipes.map((recipe) => (
                    <TabsTrigger key={recipe.id} value={recipe.id}>
                      {recipe.name} {recipe.qualifiedVersion}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {recipes.map((recipe) => (
                <TabsContent className="min-w-0" key={recipe.id} value={recipe.id}>
                  <Card className="min-w-0">
                    <CardHeader>
                      <CardTitle>
                        <h3>
                          {recipe.name} {recipe.qualifiedVersion}
                        </h3>
                      </CardTitle>
                      <CardDescription>{recipe.configurationLanguage}</CardDescription>
                    </CardHeader>
                    <CardContent className="min-w-0 space-y-7">
                      <div className="min-w-0 space-y-2">
                        <h4 className="font-medium">Configure and authorize</h4>
                        <CopyableCode
                          code={recipe.setupSnippet}
                          label={`${recipe.name} setup recipe`}
                        />
                        <p className="text-sm leading-relaxed text-muted-foreground">
                          {recipe.browserAuthorization}
                        </p>
                      </div>

                      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
                        <div className="min-w-0 space-y-2">
                          <h4 className="font-medium">Check the connection</h4>
                          <CopyableCode
                            code={recipe.connectionCheck}
                            label={`${recipe.name} connection check`}
                          />
                        </div>
                        <div className="min-w-0 space-y-2">
                          <h4 className="font-medium">Clear local access and configuration</h4>
                          <CopyableCode
                            code={recipe.localClearCommand}
                            label={`${recipe.name} local cleanup`}
                          />
                        </div>
                      </div>

                      <div className="grid gap-6 lg:grid-cols-2">
                        <div className="space-y-2">
                          <h4 className="font-medium">Restart and refresh check</h4>
                          <p className="text-sm leading-relaxed text-muted-foreground">
                            {recipe.restartAndRefresh}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-medium">Callback behavior</h4>
                          <p className="text-sm leading-relaxed text-muted-foreground">
                            {recipe.callbackGuidance}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h4 className="font-medium">Troubleshooting</h4>
                        <BulletList items={recipe.troubleshooting} />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </section>

          <section aria-labelledby="lifecycle-heading" className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2 id="lifecycle-heading">Connections and profiles</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                <p>
                  Each authorization creates a separate Passport connection. Use the client's supported
                  profile or configuration-directory environment variables when you need independent
                  accounts, grants, or concurrent client processes.
                </p>
                <p>
                  Only one process can listen on a particular callback port. Cancel a stale login before
                  retrying; for a fixed callback, stop the process holding that port. Never reuse another
                  profile's credential files.
                </p>
                <p>
                  A full client restart should reuse the stored connection. A normal Board call after
                  access expiry should refresh automatically; an authorization prompt at either point is
                  a recovery signal, not a reason to copy a token manually.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>Local cleanup versus revocation</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                <p>
                  The client commands above clear credentials and, where supported, the local server
                  configuration. Local cleanup does not revoke the corresponding Passport grant.
                </p>
                <p>
                  To revoke one connection, open Passport, go to <strong>Account</strong> →{" "}
                  <strong>Connected agents</strong>, identify that connection, and choose{" "}
                  <strong>Disconnect</strong>. Other connections remain independent.
                </p>
                <p>
                  Disconnect blocks future refresh. An access token already issued to that connection
                  can remain usable for its bounded lifetime of up to 10 minutes plus at most 60 seconds
                  of clock skew.
                </p>
              </CardContent>
            </Card>
          </section>

          <section aria-labelledby="support-heading" className="grid gap-6 lg:grid-cols-2">
            <Alert>
              <ArrowRight aria-hidden="true" />
              <AlertTitle id="support-heading">Root endpoint migration</AlertTitle>
              <AlertDescription>
                The former root operation endpoint now returns <code>410 Gone</code>. Update existing
                client configuration to the copyable <code>/mcp</code> endpoint shown above; the retired
                root is not a second Board resource identity.
              </AlertDescription>
            </Alert>

            <Alert>
              <ShieldCheck aria-hidden="true" />
              <AlertTitle>Grok Build is deferred and unsupported</AlertTitle>
              <AlertDescription>
                No actionable Grok Build recipe is published. Its native browser handoff did not reach
                Passport authorization in qualification, so support remains deferred pending a successful
                follow-up. Codex CLI, Claude Code, and OpenCode are the complete development-qualified
                matrix; production publication still requires the release replay.
              </AlertDescription>
            </Alert>
          </section>
        </div>
      </div>
    </main>
  );
}
